import { Suspense, lazy, useState, type ComponentType } from 'react';
import { DataLoadError } from '../DataLoadError';
import type { MarkdownEditorProps } from './MarkdownEditor';
import { MarkdownViewer } from './MarkdownViewer';
import * as s from './MarkdownEditor.css';
import { EditorFallback, EditorLoadBoundary } from './EditorLoadFallback';

type EditorModule = { readonly MarkdownEditor: ComponentType<MarkdownEditorProps> };
type EditorLoader = () => Promise<EditorModule | undefined>;

export function lazyMarkdownEditor(load: EditorLoader): ComponentType<MarkdownEditorProps> {
  const createLoadedEditor = () =>
    lazy(async () => {
      try {
        const module = await load();
        // Vite can resolve undefined when a preloadError listener prevents its default action.
        if (!module) throw new Error('편집기를 불러오지 못했습니다.');
        return { default: module.MarkdownEditor };
      } catch (error) {
        window.dispatchEvent(new Event('vite:preloadError'));
        throw error;
      }
    });

  function LazyMarkdownEditor(props: MarkdownEditorProps) {
    const [{ attempt, LoadedEditor }, setAttempt] = useState(() => ({
      attempt: 0,
      LoadedEditor: createLoadedEditor(),
    }));

    if (props.readOnly) return <MarkdownViewer value={props.value} />;

    const retry = () =>
      setAttempt((current) => ({
        attempt: current.attempt + 1,
        LoadedEditor: createLoadedEditor(),
      }));

    return (
      <EditorLoadBoundary
        key={attempt}
        fallback={
          <EditorFallback editorProps={props}>
            <DataLoadError
              subject="편집기"
              description="다시 시도해도 열리지 않으면 새로고침해 주세요."
              onRetry={retry}
              size="sm"
            />
          </EditorFallback>
        }
      >
        <Suspense
          fallback={
            <EditorFallback editorProps={props}>
              <div
                role="status"
                className={s.previewEmpty}
                style={{ minHeight: props.minHeight ?? 160 }}
              >
                편집기를 불러오고 있습니다.
              </div>
            </EditorFallback>
          }
        >
          <LoadedEditor {...props} />
        </Suspense>
      </EditorLoadBoundary>
    );
  }

  return LazyMarkdownEditor;
}
